import { connectRefinementList } from "instantsearch.js/es/connectors";
import type {
    RefinementListConnectorParams,
    RefinementListRenderState,
} from "instantsearch.js/es/connectors/refinement-list/connectRefinementList";
import { escapeHtml, runAfterFrame } from "./helpers";

interface ButtonFacetOptions {
    /** One or more mount points (e.g. sidebar + action-bar dropdown). */
    container: string | string[];
    attribute: string;
    sortBy?: RefinementListConnectorParams["sortBy"];
    transformItems?: RefinementListConnectorParams["transformItems"];
    /** Only show items whose `value` is in this list (e.g. seats: ['4','5','7','9']) */
    filterValues?: string[];
}

function resolveFacetContainers(selector: string | string[]): HTMLElement[] {
    const selectors = Array.isArray(selector) ? selector : [selector];
    return selectors.flatMap((sel) => {
        const el = document.querySelector<HTMLElement>(sel);
        return el ? [el] : [];
    });
}

export function createButtonFacetWidget(opts: ButtonFacetOptions) {
    const containers = resolveFacetContainers(opts.container);
    if (!containers.length) return null;

    let listenersBound = false;
    // Always refine through the latest connector callback, never a stale
    // first-render closure (mirrors make-menu / menu-style-refinement).
    let latestRefine: ((value: string) => void) | null = null;

    return connectRefinementList<{ container: HTMLElement }>(
        (
            renderOptions: RefinementListRenderState & {
                widgetParams: { container: HTMLElement };
            },
            isFirstRender: boolean,
        ) => {
            const { items, refine } = renderOptions;
            latestRefine = refine;

            let filtered = items;
            if (opts.filterValues) {
                const allowed = new Set(opts.filterValues);
                filtered = items.filter((item) => allowed.has(item.value));
            }

            const listHtml = filtered
                .map(
                    (item) => `
                <label class="facette-button${
                    item.isRefined ? " facette-button--selected" : ""
                }"
                       data-value="${escapeHtml(item.value)}">
                    <input type="checkbox"
                           value="${escapeHtml(item.value)}"
                           class="facette-button__input"
                           ${item.isRefined ? "checked" : ""} />
                    <span>${escapeHtml(item.label)}</span>
                </label>`,
                )
                .join("");

            for (const container of containers) {
                if (isFirstRender) {
                    container.innerHTML = `<div class="facette-button__wrapper">${listHtml}</div>`;
                } else {
                    const wrapper = container.querySelector(
                        ".facette-button__wrapper",
                    );
                    if (wrapper) {
                        wrapper.innerHTML = listHtml;
                    } else {
                        container.innerHTML = `<div class="facette-button__wrapper">${listHtml}</div>`;
                    }
                }
            }

            if (!listenersBound && isFirstRender) {
                listenersBound = true;
                const onChange = (e: Event) => {
                    const target = e.target as HTMLInputElement;
                    if (target.classList.contains("facette-button__input")) {
                        const value = target.value;
                        runAfterFrame(() => latestRefine?.(value));
                    }
                };
                containers.forEach((container) => {
                    container.addEventListener("change", onChange);
                });
            }
        },
    )({
        container: containers[0],
        attribute: opts.attribute,
        sortBy: opts.sortBy,
        transformItems: opts.transformItems,
    });
}
